import {
  FormErrorMessage,
  FormLabel,
  FormControl,
  InputGroup,
  Input as ChakraInput,
  Textarea as ChakraTextarea,
  Select as ChakraSelect,
  Flex,
  useBreakpointValue,
  useToken,
} from "@chakra-ui/react";
import PhoneInput from "react-phone-input-2";
import Text from "components/base/Text";
import Trans from "components/base/Trans";
import Link from "components/base/Link";
import { Controller } from "react-hook-form";
import "react-phone-input-2/lib/bootstrap.css";
import colors from "styles/colors";

export const inputStyleProps = {
  bgColor: "rgba(216, 216, 216, 0.08)",
  border: "solid 0px",
  borderBottom: "solid 1px",
  color: "white",
  borderRadius: 0,
  _hover: {
    bgColor: "rgba(216, 216, 216, 0.12)",
    borderColor: "rgba(216, 216, 216, 0.9)",
  },
  _focus: {
    bgColor: "rgba(216, 216, 216, 0.2)",
    boxShadow: "0 0 1px 1px #ffffff44",
    borderColor: "rgba(216, 216, 216, 1)",
  },
  _focusVisible: {
    boxShadow: "0 0 1px 1px #ffffff44",
  },
};

const themeColors = (themeVersion) => ({
  color: themeVersion === "black" ? "black.900" : "white",
  bg:
    themeVersion === "black"
      ? "rgba(216, 216, 216, 0.25)"
      : "rgba(216, 216, 216, 0.08)",
});

export const Input = ({ themeVersion, small, placeholder, isInvalid, ...props }) => (
  <ChakraInput
    {...inputStyleProps}
    {...themeColors(themeVersion)}
    {...props}
    fontSize={["16px", "18px"]}
    borderColor={isInvalid ? "red" : "rgba(216, 216, 216, 1)"}
    isInvalid={isInvalid}
    _placeholder={
      small ? { opacity: 1, color: "white", fontWeight: 900, fontSize: "12px" } : null
    }
    placeholder={small ? placeholder : undefined}
  />
);

export const Textarea = ({ themeVersion, name, ...props }) => (
  <ChakraTextarea
    {...inputStyleProps}
    {...themeColors(themeVersion)}
    {...props}
  />
);

export const Select = ({ options, name, ...props }) => (
  <ChakraSelect {...inputStyleProps} {...props}>
    {options?.map((option) => (
      <option key={option.value} value={option.value}>
        {option.name}
      </option>
    ))}
  </ChakraSelect>
);

const renderController = ({ phoneFontSize, bodyFont, ...props }) => {
  switch (props.type) {
    case "tel": {
      const {
        themeVersion,
        isInvalid,
        isDisabled,
        isRequired,
        error,
        small,
        label,
        ...phoneProps
      } = props;
      return (
        <Controller
          as={PhoneInput}
          disabled={isDisabled}
          style={{ opacity: isDisabled ? 0.4 : 1 }}
          preferredCountries={["gb", "us", "ca"]}
          copyNumbersOnly={false}
          disableDropdown
          inputProps={{ id: props.id, name: props.name }}
          containerStyle={{
            height: "48px",
            background:
              themeVersion === "black"
                ? "rgba(216, 216, 216, 0.25)"
                : "rgba(216, 216, 216, 0.08)",
          }}
          buttonStyle={{
            background: "transparent",
            border: "none",
          }}
          inputStyle={{
            color: themeVersion === "black" ? colors.black[900] : "white",
            background:
              themeVersion === "black"
                ? "rgba(216, 216, 216, 0.25)"
                : "rgba(216, 216, 216, 0.08)",
            borderRadius: 0,
            border: "solid 0px",
            borderBottom: "solid 1px",
            borderColor: isInvalid ? "red" : "rgba(216, 216, 216, 1)",
            height: "100%",
            maxHeight: "48px",
            width: "100%",
            fontFamily: bodyFont,
            fontSize: phoneFontSize,
            fontWeight: "inherit",
          }}
          dropdownStyle={{ color: "black" }}
          {...phoneProps}
        />
      );
    }
    case "custom":
      return props.children;
    case "textarea":
      return <Controller as={Textarea} {...props} />;
    case "select":
      return <Controller as={Select} {...props} />;
    default:
      return <Controller as={Input} {...props} />;
  }
};

export default function FormInput({
  formError,
  errorLabel,
  suggestion,
  onSuggestionClick,
  labelColor,
  ...props
}) {
  const phoneFontSize = useBreakpointValue({ base: "16px", sm: "18px" });
  const [bodyFont] = useToken("fonts", ["body"]);

  return (
    <FormControl
      isInvalid={props.error || formError}
      isDisabled={props.isDisabled}
      isRequired={props.isRequired}
    >
      <Flex direction="column">
        {!props.small && (
          <FormLabel
            htmlFor={props.id}
            color={themeColors(props.themeVersion).color}
            letterSpacing="widest"
            fontSize={["14px", "16px"]}
          >
            {props.label}
          </FormLabel>
        )}
        <InputGroup size="lg">{renderController({ ...props, phoneFontSize, bodyFont })}</InputGroup>
        {formError && <FormErrorMessage>{errorLabel}</FormErrorMessage>}
        {suggestion && formError && onSuggestionClick && (
          <Text
            type="p_sm"
            color={themeColors(props.themeVersion).color}
            mt="xxs"
            align="left"
          >
            <Trans id="LBL_FORM_REGISTER_SUGGESTION" />{" "}
            <Link onClick={onSuggestionClick}>{suggestion}</Link>
          </Text>
        )}
      </Flex>
    </FormControl>
  );
}
